import * as _ from 'lodash';
import * as moment from 'moment';
import { FormEvent, useState } from 'react';
import { IDonorProfileContact, IDonorProfileDonorData, IListItem, IState } from '@nkr/models/index';
import { DonationType, DonorType, FileDocumentType, MatchStatus, RemoteEvalStatus } from '@nkr/enums/index';
import { updateProfileDonorData } from './update-profile-donor-data';
import { Alert, Col, ControlLabel, Form, FormControl, FormGroup, Panel, Row } from 'react-bootstrap';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { IValidation } from './validation';
import { Utility } from '@nkr/services/utility';
import ReactHtmlParser from 'react-html-parser';
import YesNoUnknown from '../yes-no-unknown';
import BusinessDayAlert from '../business-date-alert';
import DonationRangeAlert from '../donation-range-alert';
import Comments from './comments';
import Kits from './kits';
import MaskedInput from 'react-maskedinput';
import RelationshipToRecipient from './relationship-to-recipient';
import IsWorkedUp from './is-worked-up';
import Alias from './alias';
import DonorFiles from './donor-files';

interface IProps {
    centerId: number,
    data: IDonorProfileDonorData,
    adminComments: string,
    isSuperAdmin: boolean,
    isCenter: boolean,
    validation: IValidation,
    surgeonList: IDonorProfileContact[],
    coordinatorList: IDonorProfileContact[],
    countryList: IListItem[],
    stateList: IState[],
    declineAdpReasonList: IListItem[],
    relationshipToPatientList: IListItem[],
    remoteEvalStatus: IListItem[],
    rdnCenterList: IListItem[],
    onChange: (data: IDonorProfileDonorData, checkDirty?: boolean) => void,
    onAdminChange: (value: string) => void,
}

const DonorDataContent = (props: IProps) => {
    const [showBusinessDayAlert, setShowBusinessDayAlert] = useState(false);
    const [showDonationRangeAlert, setShowDonationRangeAlert] = useState(false);
    const [currentDateInput, setCurrentDateInput] = useState<string>('');
    const [startExportDateInput, setStartExportDateInput] = useState<string>('');
    const [totalBusinessDays, setTotalBusinessDays] = useState<number>(0);
    const [previousDateValue, setPreviousDateValue] = useState<{ name: string; value: string; } | null>(null);
    const [alertType, setAlertType] = useState<'businessDay' | 'pastDate'>('businessDay');

    const handleSelectChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = parseInt(e.currentTarget.value);
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onNumericChange(name, value, data);

        props.onChange(data);
    };

    const handleTextChange = (e: React.FormEvent<any>) => {
        let target = e.currentTarget as HTMLInputElement | HTMLTextAreaElement;
        let name = target.name;
        let value = target.value;

        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onTextChange(name, value, data);

        props.onChange(data);
    };

    const handleDateChange = (e: React.FormEvent<any>) => {
        let target = e.currentTarget as HTMLInputElement;
        let name = target.name;
        let value = target.value;

        if (!value) {
            let data = _.cloneDeep(props.data);

            data = updateProfileDonorData.onTextChange(name, '', data);

            props.onChange(data);

            return;
        }

        value = moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY');

        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onTextChange(name, value, data);

        props.onChange(data);
    };

    const handleDateBlur = (e: React.FocusEvent<FormControl & HTMLInputElement>) => {
        if (props.isSuperAdmin) return;
        let { name, value } = e.currentTarget;

        let exportableDate = moment().format('MM/DD/YYYY');

        if (!value) return;

        let selectedDate = moment(value, 'YYYY-MM-DD', true);

        if (!selectedDate.isValid()) return;

        let today = moment().startOf('day');

        if (selectedDate.isBefore(today)) {
            setCurrentDateInput(selectedDate.format('MM/DD/YYYY'));
            setAlertType('pastDate');
            setShowBusinessDayAlert(true);
            return;
        }

        if (selectedDate.day() == 0 || selectedDate.day() == 6) {
            setCurrentDateInput(selectedDate.format('MM/DD/YYYY'));
            setAlertType('businessDay');
            setShowBusinessDayAlert(true);
            return;
        }

        let startDate = name == 'donationStartDate' ? selectedDate : moment(props.data.donationStartDate, 'MM/DD/YYYY');
        let endDate = name == 'donationEndDate' ? selectedDate : moment(props.data.donationEndDate, 'MM/DD/YYYY');

        // Chain starter rule
        let businessDaysFromExport = getBusinessDays(exportableDate, startDate);
        let startExportDate = Utility.addBusinessDays(exportableDate, 15).format("MM/DD/YYYY");
        setStartExportDateInput(startExportDate);


        if (props.data.isChainStarter) {
            if (businessDaysFromExport < 15) {
                setShowDonationRangeAlert(true);
                setTotalBusinessDays(businessDaysFromExport);
                return;
            }
        }

        if (startDate.isValid() && endDate.isValid()) {
            let bdCount = getBusinessDays(startDate, endDate);

            switch (props.data.donationType) {
                case DonationType.StandardVoucher:
                    if (bdCount < 15 || bdCount > 30) {
                        setShowDonationRangeAlert(true);
                        setTotalBusinessDays(bdCount);
                    }

                    break;

                case DonationType.FamilyVoucher:
                case DonationType.NonDirectedDonor:
                    if (bdCount < 5 || bdCount > 15) {
                        setShowDonationRangeAlert(true);
                        setTotalBusinessDays(bdCount);
                    }

                    break;
            }
        }
    };

    const handleDateFocus = (e: React.FocusEvent<any>) => {
        let target = e.currentTarget as HTMLInputElement;

        setPreviousDateValue({
            name: target.name,
            value: props.data[target.name] || ''
        });
    };

    const revertDate = () => {
        if (props.isSuperAdmin) return;
        if (!previousDateValue) return;

        let data = _.cloneDeep(props.data);

        props.onChange(data, false);
    };


    const getBusinessDays = (startDate, endDate) => {
        let start = moment(startDate).startOf('day');
        let end = moment(endDate).startOf('day');

        if (end.isBefore(start)) {
            [start, end] = [end, start];
        }

        let businessDays = 0;

        while (start.isSameOrBefore(end)) {
            const day = start.day();

            if (day !== 0 && day !== 6) {
                businessDays++;
            }

            start.add(1, 'day');
        }

        return businessDays;
    };

    const handleBooleanChange = (name: string, value: boolean) => {
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onBooleanChange(name, value, data);

        props.onChange(data);
    };

    const handleAdminCommentsChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;

        props.onAdminChange(value);
    };

    const isTrueNdd = props.data.donorType == DonorType.NonDirectedDonor && props.data.originalRecipientId == 0 && props.data.isAdp == false;
    const canChangeAlias = props.data.canChangeAlias || props.isSuperAdmin;
    const disablePrimaryCoordinator = false;
    const showRdnOption = props.data.donationType == DonationType.StandardVoucher || props.data.donationType == DonationType.DirectedDonor;
    const rdnCenterList = props.rdnCenterList.filter(x => x.id != props.data.centerId);

    return (
        <div className='container-fluid donor-profile-section donor-profile-content'>
            {(props.data.isAdp || (props.data.isDirect && !props.data.isInternalDirect)) &&
                <Row>
                    <Col xs={12} lg={12}>
                        <Panel>
                            <Panel.Heading>Voucher</Panel.Heading>
                            <Panel.Body>
                                <DonorFiles
                                    key={props.data.consentForm ? 'pending' : 'idle'}
                                    donorId={props.data.donorId}
                                    documentType={FileDocumentType.DonorAdp}
                                    pendingFile={props.data.consentForm}
                                    onFileSelected={file => props.onChange({ ...props.data, consentForm: file })}
                                    onFileRemoved={() => props.onChange({ ...props.data, consentForm: null })}
                                    onAppendChanged={append => props.onChange({ ...props.data, appendConsentForm: append })}
                                />
                                <Row>
                                    <Col xs={12} lg={3}>
                                        <label>Voucher Consent Reviewed:</label>
                                    </Col>
                                    <Col xs={12} lg={4}>
                                        <YesNoUnknown
                                            name='voucherConsentsReviewed'
                                            yes={props.data.voucherConsentsReviewed == true}
                                            no={props.data.voucherConsentsReviewed == false}
                                            unknown={props.data.voucherConsentsReviewed == null}
                                            onChange={handleBooleanChange}
                                        />
                                    </Col>
                                </Row>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            }

            <Row>
                <Col xs={12} lg={12}>
                    <Form horizontal>
                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Donation Type:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.donationTypeDescription}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Registration Date:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.registrationDate}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>First Exportable Date:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.firstExportableDate != '' ? props.data.firstExportableDate : '- not specified -'}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>MRN:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    name='mrn'
                                    maxLength={24}
                                    value={props.data.mrn}
                                    onChange={handleTextChange} />
                            </Col>

                            <Col xs={12} lg={2} componentClass={ControlLabel}>Hint/Cross Reference:</Col>
                            <Col xs={12} lg={3}>
                                <FormControl
                                    name='crossReferenceHint'
                                    maxLength={32}
                                    value={props.data.crossReferenceHint}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>

                        {props.data.matchStatus != MatchStatus.Transplanted && (
                            <>
                                {isTrueNdd && (
                                    <>
                                        <FormGroup>
                                            <Col xs={12} lg={2} componentClass={ControlLabel}>If not a Voucher Donor, why did this donor decline voucher donation?:</Col>
                                            <Col xs={12} lg={4}>
                                                <FormControl
                                                    componentClass='select'
                                                    name='declinedAdpReason'
                                                    value={props.data.declinedAdpReason}
                                                    onChange={handleSelectChange}
                                                >
                                                    <option value={0}>- Select -</option>
                                                    {props.declineAdpReasonList.map(item =>
                                                        <option key={'da-' + item.id} value={item.id}>{item.description}</option>
                                                    )}
                                                </FormControl>
                                            </Col>
                                        </FormGroup>

                                        {[1, 10].includes(props.data.declinedAdpReason) &&
                                            <FormGroup>
                                                <Col xs={12} lg={2} componentClass={ControlLabel}>Please Explain:</Col>
                                                <Col xs={12} lg={7}>
                                                    <FormControl
                                                        componentClass='textarea'
                                                        name='declinedAdpReasonOther'
                                                        value={props.data.declinedAdpReasonOther}
                                                        onChange={handleTextChange}
                                                    />
                                                </Col>
                                            </FormGroup>
                                        }
                                    </>
                                )}

                                <FormGroup>
                                    <Col xs={12} lg={2} componentClass={ControlLabel}>Donor Is Active for Matching:</Col>
                                    <Col xs={12} lg={4}>
                                        <YesNoUnknown
                                            name='isActive'
                                            yes={props.data.isActive}
                                            no={!props.data.isActive}
                                            onChange={handleBooleanChange} />
                                    </Col>
                                </FormGroup>
                            </>
                        )}

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Donor Surgeon:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='surgeonContactId'
                                    value={props.data.surgeonContactId}
                                    onChange={handleSelectChange}
                                >
                                    <option value={0}>- Select -</option>
                                    {props.surgeonList.map(surgeon =>
                                        <option key={'ds-' + surgeon.id} value={surgeon.id}>{surgeon.fullName}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        {props.data.isChainStarter && (
                            <>
                                <FormGroup>
                                    <Col xs={12} lg={2} componentClass={ControlLabel}>Earliest donation date:</Col>
                                    <Col xs={12} lg={4}>
                                        <FormControl
                                            type='date'
                                            name='donationStartDate'
                                            placeholder='mm/dd/yyyy'
                                            min={moment().format('YYYY-MM-DD')}
                                            value={
                                                props.data.donationStartDate
                                                    ? moment(props.data.donationStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
                                                    : ''
                                            }
                                            onFocus={handleDateFocus}
                                            onChange={handleDateChange}
                                            onBlur={handleDateBlur}
                                        />
                                    </Col>

                                    <Col xs={12} lg={2} componentClass={ControlLabel}>Latest donation date:</Col>
                                    <Col xs={12} lg={3}>
                                        <FormControl
                                            type='date'
                                            name='donationEndDate'
                                            placeholder='mm/dd/yyyy'
                                            min={moment().format('YYYY-MM-DD')}
                                            value={
                                                props.data.donationEndDate
                                                    ? moment(props.data.donationEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
                                                    : ''
                                            }
                                            onFocus={handleDateFocus}
                                            onChange={handleDateChange}
                                            onBlur={handleDateBlur}
                                        />
                                    </Col>
                                </FormGroup>
                            </>
                        )}

                        <IsWorkedUp
                            isWorkedUp={props.data.isWorkedUp}
                            onChange={handleBooleanChange} />

                        {props.data.donationType != DonationType.FamilyVoucher && props.data.donationType != DonationType.NonDirectedDonor && (
                            <RelationshipToRecipient
                                relationshipToRecipient={props.data.relationshipToRecipient}
                                relationshipToPatientOther={props.data.relationshipToPatientOther}
                                relationshipToPatientList={props.relationshipToPatientList}
                                onTextChange={handleTextChange}
                            />
                        )}

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>UNOS Donor ID:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    name='unosId'
                                    maxLength={7}
                                    value={props.data.unosId}
                                    onChange={handleTextChange} />
                            </Col>
                            <Col xs={12} lg={4}>
                                <OverlayTrigger trigger={['hover', 'click']} placement='bottom'
                                    overlay={<Popover>Please be advised that any changes to the UNOS Donor ID will
                                        be shared with the recipient center involved.</Popover>}>
                                    <span className='glyphicon glyphicon-info-sign'></span>
                                </OverlayTrigger>
                            </Col>
                        </FormGroup>

                        <Alias
                            alias={props.data.alias}
                            allowEdit={canChangeAlias}
                            isValid={props.validation.alias}
                            onChange={handleTextChange}
                        />

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Primary Coordinator:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='coordinatorId'
                                    value={props.data.coordinatorId}
                                    onChange={handleSelectChange}
                                    disabled={disablePrimaryCoordinator}
                                >
                                    <option value={0}>Default Coordinator</option>
                                    {props.coordinatorList.map(coordinator =>
                                        <option key={'dc-' + coordinator.id} value={coordinator.id}>{coordinator.fullName}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        {props.data.isRemotePhlebo && (
                            <FormGroup>
                                <Col xs={12} lg={2} componentClass={ControlLabel}>Use Traveling Phlebotomist:</Col>
                                <Col xs={12} lg={7}>
                                    <YesNoUnknown
                                        name='usePhlebotomy'
                                        yes={props.data.usePhlebotomy}
                                        no={!props.data.usePhlebotomy}
                                        onChange={handleBooleanChange} />
                                </Col>
                            </FormGroup>
                        )}
                    </Form>

                    <br />

                    <Kits
                        directCryoSend={props.data.directCryoSend}
                        directXmSend={props.data.directXmSend}
                        directSerologySend={props.data.directSerologySend}
                        onBooleanChange={handleBooleanChange}
                    />
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={12}>
                    <Form horizontal>
                        {props.centerId == props.data.rdnCenterId && props.data.donationType != DonationType.NonDirectedDonor ?
                            <>
                                {props.data.remoteWorkupDate == '' ?
                                    <>
                                        <FormGroup>
                                            <Col xs={12} lg={2} componentClass={ControlLabel}>Remote Workup Status:</Col>
                                            <Col xs={12} lg={7}>
                                                <FormControl
                                                    componentClass='select'
                                                    name='didRemoteWorkup'
                                                    value={props.data.didRemoteWorkup}
                                                    onChange={handleSelectChange}
                                                >
                                                    <option value={0}>- Select -</option>
                                                    {props.remoteEvalStatus.map(item =>
                                                        <option key={'rws-' + item.id} value={item.id}>{item.description}</option>
                                                    )}
                                                </FormControl>
                                            </Col>
                                        </FormGroup>

                                        {props.data.didRemoteWorkup == RemoteEvalStatus.YesWillDonate && (
                                            <FormGroup>
                                                <Col xs={12} lg={2} componentClass={ControlLabel}></Col>
                                                <Col xs={12} lg={7}>
                                                    <Alert bsStyle='info'>You will be reimbursed for the eval after donation by the kidney's recipient center.</Alert>
                                                </Col>
                                            </FormGroup>
                                        )}

                                        {props.data.didRemoteWorkup == RemoteEvalStatus.YesWillNotDonate && (
                                            <FormGroup>
                                                <Col xs={12} lg={2} componentClass={ControlLabel}>Workup Completed Date:</Col>
                                                <Col xs={12} lg={7}>
                                                    <MaskedInput
                                                        name='remoteWorkupDate'
                                                        className='form-control'
                                                        mask='11/11/1111'
                                                        placeholder='mm/dd/yyyy'
                                                        value={props.data.remoteWorkupDate}
                                                        onChange={handleTextChange}
                                                    />
                                                </Col>
                                            </FormGroup>
                                        )}
                                    </>
                                    :
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Workup Completed Date:</Col>
                                        <Col xs={12} lg={7}>
                                            <div className='form-control-static'>{props.data.remoteWorkupDate}</div>
                                        </Col>
                                    </FormGroup>
                                }
                            </>
                            :
                            <>
                                {showRdnOption &&
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>RDN Managing Center:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                componentClass='select'
                                                name='rdnCenterId'
                                                value={props.data.rdnCenterId}
                                                onChange={handleSelectChange}
                                            >
                                                <option value={0}>- Not a Remote Donor -</option>
                                                {rdnCenterList.map(center =>
                                                    <option key={'rdn-' + center.id} value={center.id}>{center.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroup>
                                }
                            </>
                        }
                    </Form>
                </Col>
            </Row>

            {props.isCenter &&
                <>
                    {props.data.comments != '' &&
                        <Row>
                            <Col xs={12} lg={12}>
                                {ReactHtmlParser(props.data.comments)}
                            </Col>
                        </Row>
                    }

                    <Row>
                        <Col xs={12} lg={12}>
                            <Comments
                                adminComments={props.adminComments}
                                centerId={props.centerId}
                                donorId={props.data.donorId}
                                onChange={handleAdminCommentsChange}
                            />
                        </Col>
                    </Row>
                </>
            }

            <BusinessDayAlert
                show={showBusinessDayAlert}
                datechosen={currentDateInput}
                alertType={alertType}
                onClose={() => {
                    setShowBusinessDayAlert(false);
                    revertDate();
                }}
            />

            <DonationRangeAlert
                show={showDonationRangeAlert}
                startExportDate={startExportDateInput}
                donationStartDate={props.data.donationStartDate}
                donationEndDate={props.data.donationEndDate}
                donationType={props.data.donationType}
                isChainStarter={props.data.isChainStarter}
                totalBusinessDays={totalBusinessDays}
                onClose={() => {
                    setShowDonationRangeAlert(false);
                    revertDate();
                }}
            />
        </div>
    )
}

export default DonorDataContent;